import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { AddIcon, ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	Divider,
	Flex,
	FormControl,
	FormLabel,
	Grid,
	GridItem,
	Icon,
	Input,
	InputGroup,
	InputRightElement,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Stack,
	Text,
	Tooltip,
	useDisclosure,
	useToast,
} from "@chakra-ui/react";
import { FaBolt, FaCity, FaFire, FaMoneyBillWave, FaTint, FaTrash, FaWifi } from "react-icons/fa";

import { addAccountAsync, removeAccountAsync } from "../../redux/user/userSlice";
import { addAccountVerifier } from "../../utils/verifier";

const accountTypes = [
	{ id: "cityHall", label: "Primarie", icon: FaCity },
	{ id: "finances", label: "Finante", icon: FaMoneyBillWave },
	{ id: "water", label: "Apa", icon: FaTint },
	{ id: "gas", label: "Gaz", icon: FaFire },
	{ id: "electricity", label: "Curent", icon: FaBolt },
	{ id: "internet", label: "Internet", icon: FaWifi },
];

export default function AccountsTab({ property }) {
	const { t } = useTranslation();
	const isArchived = property.archived;
	const { isOpen, onOpen, onClose } = useDisclosure();
	const accounts = property.accounts || [];
	const initialAccountInformation = { userName: "", type: "", password: "", propertyId: property.id, provider: "" };
	const [accountInformation, setAccountInformation] = useState(initialAccountInformation);
	const dispatch = useDispatch();
	const [error, setError] = useState({});
	const toast = useToast();
	const [showModalPassword, setShowModalPassword] = useState(false);
	const [visiblePasswords, setVisiblePasswords] = useState({});

	const togglePasswordVisibility = (accountId) => {
		setVisiblePasswords((prev) => ({
			...prev,
			[accountId]: !prev[accountId],
		}));
	};

	const addAccount = () => {
		const errors = addAccountVerifier(accountInformation);
		if (Object.entries(errors).length !== 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(addAccountAsync(accountInformation))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Account added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setAccountInformation(initialAccountInformation);
				onClose();
			});
	};

	const handleAccountDelete = (account) => {
		const payload = { ...account, propertyId: property.id };
		dispatch(removeAccountAsync(payload));
	};

	const handleChange = (e) => {
		setAccountInformation({ ...accountInformation, [e.target.name]: e.target.value });
	};

	const renderAccountCard = (account) => {
		const accountType = accountTypes.find((t) => t.id === account.type);
		const isPasswordVisible = visiblePasswords[account.type] || false;

		return (
			<Card variant="elevated" size="sm" key={account.type}>
				<CardHeader pb={2}>
					<Flex justify="space-between" align="center">
						<Flex align="center" gap={2}>
							<Icon as={accountType?.icon} boxSize={5} color="blue.500" />
							<Text fontWeight="bold">{t(`types.${account.type}`)}</Text>
						</Flex>
						<Tooltip label={t("table.delete")} placement="top">
							<Button size="sm" colorScheme="red" variant="ghost" onClick={() => handleAccountDelete(account)} isDisabled={isArchived}>
								<Icon as={FaTrash} />
							</Button>
						</Tooltip>
					</Flex>
				</CardHeader>
				<CardBody pt={0}>
					<Stack spacing={2}>
						<Flex justify="space-between">
							<Text color="gray.500">{t("property.provider")}:</Text>
							<Text>{account.provider || "-"}</Text>
						</Flex>
						<Flex justify="space-between">
							<Text color="gray.500">{t("property.userName")}:</Text>
							<Text>{account.userName}</Text>
						</Flex>
						<Flex justify="space-between" align="center">
							<Text color="gray.500">{t("property.password")}:</Text>
							<Flex align="center" gap={2}>
								<Text>{isPasswordVisible ? account.password : "••••••••"}</Text>
								<Tooltip label={isPasswordVisible ? t("misc.hidePassword") : t("misc.showPassword")} placement="top">
									<Button size="xs" variant="ghost" onClick={() => togglePasswordVisibility(account.type)}>
										<Icon as={isPasswordVisible ? ViewOffIcon : ViewIcon} />
									</Button>
								</Tooltip>
							</Flex>
						</Flex>
					</Stack>
				</CardBody>
			</Card>
		);
	};

	const renderAddAccountModal = () => (
		<Modal isOpen={isOpen} onClose={onClose} size="md">
			<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
			<ModalContent>
				<ModalHeader>
					<Text>{t("property.addAccount")}</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Stack spacing={4}>
						<FormControl isInvalid={error?.type}>
							<FormLabel>{t("property.accountType")}</FormLabel>
							<Select name="type" value={accountInformation.type} onChange={handleChange} placeholder={t("property.selectAccountType")}>
								{accountTypes.map((type) => (
									<option key={type.id} value={type.id}>
										{type.label}
									</option>
								))}
							</Select>
						</FormControl>
						<FormControl isInvalid={error?.provider}>
							<FormLabel>{t("property.provider")}</FormLabel>
							<Input name="provider" value={accountInformation.provider} onChange={handleChange} placeholder={t("property.enterProvider")} />
						</FormControl>
						<FormControl isInvalid={error?.userName}>
							<FormLabel>{t("property.userName")}</FormLabel>
							<Input name="userName" value={accountInformation.userName} onChange={handleChange} placeholder={t("property.enterUsername")} />
						</FormControl>
						<FormControl isInvalid={error?.password}>
							<FormLabel>{t("property.password")}</FormLabel>
							<InputGroup>
								<Input
									name="password"
									type={showModalPassword ? "text" : "password"}
									value={accountInformation.password}
									onChange={handleChange}
									placeholder={t("property.enterPassword")}
								/>
								<InputRightElement>
									<Button size="sm" variant="ghost" onClick={() => setShowModalPassword(!showModalPassword)}>
										<Icon as={showModalPassword ? ViewOffIcon : ViewIcon} />
									</Button>
								</InputRightElement>
							</InputGroup>
						</FormControl>
					</Stack>
				</ModalBody>
				<ModalFooter>
					<Button variant="ghost" mr={3} onClick={onClose}>
						{t("misc.cancel")}
					</Button>
					<Button colorScheme="blue" onClick={addAccount}>
						{t("misc.save")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);

	return (
		<Stack spacing={6}>
			<Flex justify="space-between" align="center">
				<Tooltip label={t("property.addAccount")} placement="top">
					<Button leftIcon={<AddIcon />} colorScheme="blue" onClick={onOpen} isDisabled={isArchived}>
						{t("property.addAccount")}
					</Button>
				</Tooltip>
			</Flex>
			<Divider />
			{accounts.length === 0 ? (
				<Text color="gray.500" textAlign="center" py={4}>
					{t("table.noData")}
				</Text>
			) : (
				<Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
					{accounts.map((account) => (
						<GridItem key={account.type}>{renderAccountCard(account)}</GridItem>
					))}
				</Grid>
			)}
			{renderAddAccountModal()}
		</Stack>
	);
}
